// ABOUTME: Authentication context using the NS gateway cookie-session model.
// ABOUTME: Access token lives in memory; boot does a silent refresh off the httpOnly cookie.

import { createContext, useState, useEffect, type ReactNode } from 'react';
import {
  api,
  setAccessToken,
  setStoredUser,
  getStoredUser,
  clearStoredAuth,
  decodeAccessClaims,
  refreshAccessToken,
  getCsrfToken,
} from '@/lib/supabase';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:7777';

export interface NSUser {
  id: string;
  email: string;
  org_id: string;
  role: string;
  name?: string;
}

interface AuthContextType {
  user: NSUser | null;
  isLoading: boolean;
  sendOtp: (email: string) => Promise<{ debugToken?: string }>;
  verifyOtp: (email: string, token: string) => Promise<void>;
  logout: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<NSUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Boot-time silent refresh: exchange the httpOnly refresh cookie for a fresh
  // access token. On success, populate the user from the token claims (with the
  // cached user as a fallback for fields not in the JWT, e.g. name). On failure
  // (401 / no cookie), treat as logged-out.
  useEffect(() => {
    let cancelled = false;

    (async () => {
      const token = await refreshAccessToken();
      if (cancelled) return;

      if (token) {
        const claims = decodeAccessClaims(token);
        const cached = getStoredUser();
        const nsUser: NSUser = {
          id: claims.user_id || cached?.id || '',
          email: claims.email || cached?.email || '',
          org_id: claims.org_id || cached?.org_id || '',
          role: claims.role || cached?.role || 'member',
        };
        setStoredUser(nsUser);
        setUser(nsUser);
      } else {
        clearStoredAuth();
        setUser(null);
      }

      setIsLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const sendOtp = async (email: string): Promise<{ debugToken?: string }> => {
    const res = await api.post<{ message: string; _debug_token?: string }>(
      '/auth/login',
      { email },
    );
    return { debugToken: res._debug_token };
  };

  const verifyOtp = async (email: string, token: string) => {
    // The gateway sets the httpOnly refresh + JS-readable csrf cookies on this
    // response, so the request must carry credentials. api.post already sends
    // credentials:'include'.
    const res = await api.post<{
      token: string;
      user_id: string;
      org_id: string;
      expires_in?: number;
    }>('/auth/verify', { token });

    setAccessToken(res.token);
    const claims = decodeAccessClaims(res.token);

    const nsUser: NSUser = {
      id: res.user_id,
      email: claims.email || email,
      org_id: res.org_id,
      role: claims.role || 'member',
    };

    setStoredUser(nsUser);
    setUser(nsUser);
  };

  const logout = async () => {
    // CSRF-protected for cookie clients. Fire directly so we can attach the
    // X-CSRF-Token header; ignore network/CSRF errors — we clear locally either way.
    try {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      const csrf = getCsrfToken();
      if (csrf) headers['X-CSRF-Token'] = csrf;

      await fetch(`${API_URL}/auth/logout`, {
        method: 'POST',
        headers,
        credentials: 'include',
      });
    } catch {
      // Best-effort; cookies may already be gone.
    }

    clearStoredAuth();
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, isLoading, sendOtp, verifyOtp, logout }}>
      {children}
    </AuthContext.Provider>
  );
}
